import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import CeoKpisPanel from "@/components/pilotage/CeoKpisPanel";
import CeoLandingPanel from "@/components/pilotage/CeoLandingPanel";
import CeoLeversPanel from "@/components/pilotage/CeoLeversPanel";
import CeoSummaryCards from "@/components/pilotage/CeoSummaryCards";
import ChargesFixesRentabilite from "@/components/pilotage/ChargesFixesRentabilite";
import ObjectivePlanningReference from "@/components/pilotage/ObjectivePlanningReference";
import MonthlyObjectivesEditor from "@/components/pilotage/MonthlyObjectivesEditor";
import { PLAN_LABELS, planTone } from "@/lib/monthlyObjectives";
import ObjectivesExecutiveSummary from "@/components/pilotage/ObjectivesExecutiveSummary";
import ObjectivesKpiTable from "@/components/pilotage/ObjectivesKpiTable";
import ObjectivesPipeline from "@/components/pilotage/ObjectivesPipeline";
import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AppEmptyState from "@/components/ui/AppEmptyState";
import AppErrorState from "@/components/ui/AppErrorState";
import AppLoadingState from "@/components/ui/AppLoadingState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ADMIN_KPI_REGISTRY } from "@/admin/config/adminKpiNaming.ts";
import { getApiErrorMessage } from "@/lib/api";
import {
  createObjective,
  deleteObjective,
  getPilotageCockpit,
  listObjectivesByYear,
  listObjectiveYears,
  updateObjective,
} from "@/lib/pilotageApi";
import {
  normalizePctInputToRate,
  parseLocaleNumber,
} from "@/lib/businessRules";
import { formatEUR0, formatNumber, formatPct0, normalizePercentValue } from "@/lib/pilotageUi";

const CREATE_OBJECTIVE_SENTINEL = "__create_objective__";
const CEO_VIEW_STORAGE_KEY = "pilotage-objectives-ceo-view";

const METRIC_CONFIG = [
  {
    key: "annual_revenue_target",
    label: ADMIN_KPI_REGISTRY.ca_annuel_net.label,
    group: "CA",
    formatter: (value) => formatEUR0(value),
    step: "1000",
    placeholder: "1000000",
    hint: "Objectif de chiffre d'affaires annuel net d'intermédiaires.",
  },
  {
    key: "tjm_target",
    label: ADMIN_KPI_REGISTRY.tjm_moyen_cible.label,
    group: "KPI Pilotage",
    formatter: (value) => formatEUR0(value),
    step: "1",
    placeholder: "550",
    hint: "Objectif de TJM net moyen journalier.",
  },
  {
    key: "margin_target",
    label: "Marge % cible",
    group: "KPI Pilotage",
    formatter: (value) => formatPct0(value),
    step: "0.1",
    placeholder: "25",
    inputType: "text",
    inputMode: "decimal",
    isPercentInput: true,
    hint: "Objectif de taux de marge net cible.",
  },
  {
    key: "fixed_costs_target",
    label: "Charges fixes prévues",
    group: "Charges",
    formatter: (value) => formatEUR0(value),
    step: "1000",
    placeholder: "Ex : 294900",
    hint: "Montant annuel de charges fixes prévu dans ce business plan.",
  },
  {
    key: "forecast_result_target",
    label: "Résultat prévisionnel",
    group: "Résultat",
    formatter: (value) => formatEUR0(value),
    step: "1000",
    placeholder: "Ex : 25000",
    allowNegative: true,
    hint: "Résultat net annuel attendu dans ce business plan (peut être négatif).",
  },
  {
    key: "total_resources_end_year",
    label: "Ressources fin d'année",
    group: "Ressources",
    formatter: (value) => formatNumber(value, { maximumFractionDigits: 0 }),
    step: "1",
    placeholder: "15",
    hint: "Objectif d'effectifs en fin d'année.",
  },
  {
    key: "salaried_resources_end_year",
    label: "Salariés fin d'année",
    group: "Ressources",
    formatter: (value) => formatNumber(value, { maximumFractionDigits: 0 }),
    step: "1",
    placeholder: "10",
    hint: "Objectif de salariés (CDI/CDD/Alternants) en fin d'année.",
  },
  {
    key: "external_resources_end_year",
    label: "Externes fin d'année",
    group: "Ressources",
    formatter: (value) => formatNumber(value, { maximumFractionDigits: 0 }),
    step: "1",
    placeholder: "5",
    hint: "Objectif de ressources externes (Freelances/Portage/Sous-traitants) en fin d'année.",
  },
  {
    key: "entries_per_month",
    label: "Entrées / mois (ETP)",
    group: "Ressources",
    formatter: (value) => formatNumber(value, { maximumFractionDigits: 2 }),
    step: "0.1",
    placeholder: "0.5",
    hint: "Objectif de recrutement mensuel (ETP).",
  },
  {
    key: "total_annual_billed_etp",
    label: ADMIN_KPI_REGISTRY.etp_factures_annuels.label,
    group: "ETP",
    formatter: (value) => formatNumber(value, { maximumFractionDigits: 2 }),
    step: "0.1",
    placeholder: "8",
    hint: "Objectif d'ETP facturés sur l'année.",
  },
  {
    key: "avg_new_billed_etp",
    label: "Moyenne nouveaux ETP facturés",
    group: "ETP",
    formatter: (value) => formatNumber(value, { maximumFractionDigits: 2 }),
    step: "0.1",
    placeholder: "0.7",
    hint: "Objectif de moyenne de nouveaux ETP facturés.",
  },
];

const buildEmptyMetrics = () =>
  METRIC_CONFIG.reduce((accumulator, metric) => {
    accumulator[metric.key] = null;
    return accumulator;
  }, {});

const EMPTY_METRICS = buildEmptyMetrics();

const buildInitialFormValues = (objective = null) => {
  const targets = objective?.annual_targets || objective?.targets || {};
  return {
    ...Object.fromEntries(
      METRIC_CONFIG.map(({ key, isPercentInput }) => [
        key,
        toFormNumberValue(targets[key], { asPercent: Boolean(isPercentInput) }),
      ]),
    ),
    notes: objective?.notes || "",
  };
};

const toFormNumberValue = (value, { asPercent = false } = {}) => {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return "";
  }

  const displayValue = asPercent ? numericValue * 100 : numericValue;
  return Number.isFinite(displayValue) ? String(displayValue) : "";
};

const normalizeObjectivesPayload = (payload) => {
  const rawObjectives = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.objectives)
      ? payload.objectives
      : [];

  return rawObjectives
    .filter((objective) => objective?.id && objective?.name)
    .map((objective) => ({
      ...objective,
      id: String(objective.id),
      name: String(objective.name),
      slug: String(objective.slug || ""),
      is_default: Boolean(objective.is_default),
      targets: {
        ...EMPTY_METRICS,
        ...(objective.targets || {}),
      },
      notes: objective.notes || null,
    }));
};

const resolveDefaultObjective = (objectives) => {
  if (!objectives.length) {
    return null;
  }
  return objectives.find((objective) => objective.is_default) || objectives[0];
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const parseTargetInputValue = (metric, rawValue) => {
  const parsedValue = parseLocaleNumber(rawValue);

  if (parsedValue === null) {
    return null;
  }

  if (!metric?.allowNegative && parsedValue < 0) {
    return Number.NaN;
  }

  if (metric?.isPercentInput) {
    const ratio = normalizePctInputToRate(parsedValue, null);
    if (ratio === null || ratio < 0) {
      return Number.NaN;
    }
    return ratio;
  }

  return parsedValue;
};

const toSignedValue = (value, formatter) => {
  if (!Number.isFinite(value)) {
    return "";
  }

  const prefix = value > 0 ? "+" : "";
  return `${prefix}${formatter(value)}`;
};

const computeStatusFromPercent = (percentValue) => {
  if (!Number.isFinite(percentValue)) {
    return "N/A";
  }
  if (percentValue >= 95) {
    return "OK";
  }
  if (percentValue >= 80) {
    return "WARN";
  }
  return "BAD";
};

const isDuplicateObjectiveError = (error) => {
  if (!error) {
    return false;
  }

  // Only treat 409 Conflict as a duplicate — the backend always returns 409
  // with detail "objective_slug_already_exists" for real slug collisions.
  const status = error.response?.status;
  if (status !== 409) {
    return false;
  }

  const detail = error.response?.data?.detail;
  return detail === "objective_slug_already_exists";
};

const slugifyObjectiveName = (name) => {
  const slug = String(name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || "";
};

const createStatusPayloadFromCockpit = (payload = {}, selectedObjective = null) => {
  const contextObjective = payload?.context?.objective || {};
  const cockpitObjectives = payload?.objectives || {};
  const comparisons = payload?.comparisons || {};
  const staffing = payload?.staffing || {};
  const finance = payload?.finance || {};

  const targets = {
    ...EMPTY_METRICS,
    annual_revenue_target: toFiniteNumber(cockpitObjectives?.target_ca_annual_net),
    tjm_target: toFiniteNumber(cockpitObjectives?.target_tjm_net),
    margin_target: toFiniteNumber(cockpitObjectives?.target_margin_pct),
    total_resources_end_year: toFiniteNumber(cockpitObjectives?.target_resources_end_year),
    salaried_resources_end_year: toFiniteNumber(cockpitObjectives?.target_salaried_resources_end_year),
    external_resources_end_year: toFiniteNumber(cockpitObjectives?.target_external_resources_end_year),
    entries_per_month: toFiniteNumber(cockpitObjectives?.target_entries_per_month),
    total_annual_billed_etp: toFiniteNumber(cockpitObjectives?.target_annual_billed_etp),
    forecast_result_target: toFiniteNumber(cockpitObjectives?.target_forecast_result),
    avg_new_billed_etp: toFiniteNumber(cockpitObjectives?.target_avg_new_billed_etp),
    fixed_costs_target: toFiniteNumber(cockpitObjectives?.target_fixed_costs),
  };

  const realized = {
    ...EMPTY_METRICS,
    annual_revenue_target: toFiniteNumber(comparisons?.ca_annual_net?.actual ?? finance?.ca_annual_net),
    tjm_target: toFiniteNumber(comparisons?.tjm_net?.actual ?? finance?.average_tjm_net),
    margin_target: toFiniteNumber(comparisons?.margin_pct?.actual ?? finance?.average_margin_pct),
    total_resources_end_year: toFiniteNumber(comparisons?.resources_end_year?.actual ?? staffing?.resources_end_year),
    salaried_resources_end_year: toFiniteNumber(comparisons?.salaried_resources_end_year?.actual ?? staffing?.salaried_resources_end_year),
    external_resources_end_year: toFiniteNumber(comparisons?.external_resources_end_year?.actual ?? staffing?.external_resources_end_year),
    entries_per_month: toFiniteNumber(comparisons?.entries_per_month?.actual ?? staffing?.entries_per_month),
    total_annual_billed_etp: toFiniteNumber(comparisons?.annual_billed_etp?.actual ?? staffing?.annual_billed_etp),
    avg_new_billed_etp: toFiniteNumber(comparisons?.avg_new_billed_etp?.actual ?? staffing?.average_new_billed_etp),
    forecast_result_target: toFiniteNumber(comparisons?.forecast_result?.actual ?? finance?.net_profit_annual),
    fixed_costs_target: toFiniteNumber(comparisons?.fixed_costs?.actual ?? finance?.fixed_costs_annual_total),
  };

  const delta = {
    ...EMPTY_METRICS,
    annual_revenue_target: toFiniteNumber(comparisons?.ca_annual_net?.delta),
    tjm_target: toFiniteNumber(comparisons?.tjm_net?.delta),
    margin_target: toFiniteNumber(comparisons?.margin_pct?.delta),
    total_resources_end_year: toFiniteNumber(comparisons?.resources_end_year?.delta),
    salaried_resources_end_year: toFiniteNumber(comparisons?.salaried_resources_end_year?.delta),
    external_resources_end_year: toFiniteNumber(comparisons?.external_resources_end_year?.delta),
    entries_per_month: toFiniteNumber(comparisons?.entries_per_month?.delta),
    total_annual_billed_etp: toFiniteNumber(comparisons?.annual_billed_etp?.delta),
    forecast_result_target: toFiniteNumber(comparisons?.forecast_result?.delta),
    avg_new_billed_etp: toFiniteNumber(comparisons?.avg_new_billed_etp?.delta),
    fixed_costs_target: toFiniteNumber(comparisons?.fixed_costs?.delta),
  };

  const percent = {
    ...EMPTY_METRICS,
    annual_revenue_target: toFiniteNumber(comparisons?.ca_annual_net?.percent),
    tjm_target: toFiniteNumber(comparisons?.tjm_net?.percent),
    margin_target: toFiniteNumber(comparisons?.margin_pct?.percent),
    total_resources_end_year: toFiniteNumber(comparisons?.resources_end_year?.percent),
    salaried_resources_end_year: toFiniteNumber(comparisons?.salaried_resources_end_year?.percent),
    external_resources_end_year: toFiniteNumber(comparisons?.external_resources_end_year?.percent),
    entries_per_month: toFiniteNumber(comparisons?.entries_per_month?.percent),
    total_annual_billed_etp: toFiniteNumber(comparisons?.annual_billed_etp?.percent),
    forecast_result_target: toFiniteNumber(comparisons?.forecast_result?.percent),
    avg_new_billed_etp: toFiniteNumber(comparisons?.avg_new_billed_etp?.percent),
    fixed_costs_target: toFiniteNumber(comparisons?.fixed_costs?.percent),
  };

  const status = {
    annual_revenue_target: String(comparisons?.ca_annual_net?.status || "N/A"),
    tjm_target: String(comparisons?.tjm_net?.status || "N/A"),
    margin_target: String(comparisons?.margin_pct?.status || "N/A"),
    total_resources_end_year: String(comparisons?.resources_end_year?.status || "N/A"),
    salaried_resources_end_year: String(comparisons?.salaried_resources_end_year?.status || "N/A"),
    external_resources_end_year: String(comparisons?.external_resources_end_year?.status || "N/A"),
    entries_per_month: String(comparisons?.entries_per_month?.status || "N/A"),
    total_annual_billed_etp: String(comparisons?.annual_billed_etp?.status || "N/A"),
    avg_new_billed_etp: String(comparisons?.avg_new_billed_etp?.status || "N/A"),
    forecast_result_target: String(comparisons?.forecast_result?.status || "N/A"),
    fixed_costs_target: String(comparisons?.fixed_costs?.status || "N/A"),
  };

  return {
    id: String(contextObjective?.id || selectedObjective?.id || ""),
    year: Number(payload?.context?.current_year) || new Date().getFullYear(),
    name: String(contextObjective?.name || selectedObjective?.name || ""),
    slug: String(selectedObjective?.slug || ""),
    targets,
    realized,
    delta,
    percent,
    status,
  };
};

const ObjectivesPage = () => {
  const fallbackYear = new Date().getFullYear();
  const [years, setYears] = useState([fallbackYear]);
  const [selectedYear, setSelectedYear] = useState(() => {
    const requested = Number(new URLSearchParams(window.location.search).get("year"));
    return [2026, 2027].includes(requested) ? requested : fallbackYear;
  });
  const [objectives, setObjectives] = useState([]);
  const [activeObjectiveId, setActiveObjectiveId] = useState(() => new URLSearchParams(window.location.search).get("objective_id") || "");
  const [statusPayload, setStatusPayload] = useState(null);
  const [cockpitPayload, setCockpitPayload] = useState(null);
  const [loadingYears, setLoadingYears] = useState(true);
  const [loadingObjectives, setLoadingObjectives] = useState(false);
  const [loadingCockpit, setLoadingCockpit] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCeoView, setIsCeoView] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    const storedValue = window.localStorage.getItem(CEO_VIEW_STORAGE_KEY);
    return storedValue !== "0";
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [focusField, setFocusField] = useState(null);

  useEffect(() => {
    if (editModalOpen && focusField) {
      const timer = setTimeout(() => {
        const inputElement = document.getElementById(`objective-${focusField}`);
        if (inputElement) {
          inputElement.focus();
          inputElement.select?.();
        }
        setFocusField(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [editModalOpen, focusField]);

  const [savingEdit, setSavingEdit] = useState(false);
  const [formValues, setFormValues] = useState(buildInitialFormValues);
  const [formError, setFormError] = useState("");

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newObjectiveName, setNewObjectiveName] = useState("");
  const [createError, setCreateError] = useState("");
  const [creatingObjective, setCreatingObjective] = useState(false);

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState("");
  const [renamingObjective, setRenamingObjective] = useState(false);

  const [deletingObjective, setDeletingObjective] = useState(false);

  const loadYears = useCallback(async () => {
    setLoadingYears(true);
    setErrorMessage("");

    try {
      const payload = await listObjectiveYears();
      const currentYear = Number(payload?.current_year);
      const yearsFromApi = Array.isArray(payload?.years) ? payload.years : [];
      const dedupedYears = Array.from(
        new Set([
          2026, 2027,
          ...yearsFromApi.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value >= 2026),
        ]),
      ).sort((left, right) => left - right);

      setYears(dedupedYears);

      if (!dedupedYears.includes(selectedYear)) {
        if (Number.isFinite(currentYear) && dedupedYears.includes(currentYear)) {
          setSelectedYear(currentYear);
        } else {
          setSelectedYear(dedupedYears[dedupedYears.length - 1]);
        }
      }
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, "Impossible de charger les années d'objectifs."));
    } finally {
      setLoadingYears(false);
    }
  }, [selectedYear]);

  const loadObjectives = useCallback(async (year) => {
    setLoadingObjectives(true);

    try {
      const payload = await listObjectivesByYear(year);
      const normalizedObjectives = normalizeObjectivesPayload(payload);
      setObjectives(normalizedObjectives);
      return normalizedObjectives;
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, "Impossible de charger les objectifs annuels."));
      setObjectives([]);
      return [];
    } finally {
      setLoadingObjectives(false);
    }
  }, []);

  const loadCockpit = useCallback(async ({ year, objectiveId, fallbackObjective = null }) => {
    setLoadingCockpit(true);

    try {
      const payload = await getPilotageCockpit({
        year,
        objective_id: objectiveId || undefined,
      });
      setCockpitPayload(payload || null);
      setStatusPayload(createStatusPayloadFromCockpit(payload, fallbackObjective));
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, "Impossible de charger la source cockpit pilotage."));
      setCockpitPayload(null);
      setStatusPayload(null);
    } finally {
      setLoadingCockpit(false);
    }
  }, []);

  const refreshObjectives = useCallback(async ({ preferredObjectiveId = null } = {}) => {
    const refreshedObjectives = await loadObjectives(selectedYear);

    if (!refreshedObjectives.length) {
      setActiveObjectiveId("");
      setStatusPayload(null);
      return [];
    }

    const preferredObjective =
      preferredObjectiveId && refreshedObjectives.find((objective) => objective.id === preferredObjectiveId);
    const currentActiveObjective = refreshedObjectives.find((objective) => objective.id === activeObjectiveId);
    const defaultObjective = resolveDefaultObjective(refreshedObjectives);

    const nextActiveObjective = preferredObjective || currentActiveObjective || defaultObjective;
    setActiveObjectiveId(nextActiveObjective?.id || "");

    return refreshedObjectives;
  }, [activeObjectiveId, loadObjectives, selectedYear]);

  useEffect(() => {
    void loadYears();
  }, [loadYears]);

  useEffect(() => {
    if (!selectedYear) {
      return;
    }

    void refreshObjectives();
  }, [selectedYear, refreshObjectives]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(CEO_VIEW_STORAGE_KEY, isCeoView ? "1" : "0");
  }, [isCeoView]);

  const selectedObjective = useMemo(() => {
    if (!activeObjectiveId) {
      return null;
    }

    return objectives.find((objective) => objective.id === activeObjectiveId) || null;
  }, [activeObjectiveId, objectives]);

  useEffect(() => {
    if (!selectedYear) {
      return;
    }

    void loadCockpit({
      year: selectedYear,
      objectiveId: activeObjectiveId,
      fallbackObjective: selectedObjective,
    });
  }, [activeObjectiveId, loadCockpit, selectedObjective, selectedYear]);

  const tableMetrics = useMemo(() => {
    const targets = statusPayload?.targets || selectedObjective?.targets || EMPTY_METRICS;
    const realized = statusPayload?.realized || EMPTY_METRICS;
    const delta = statusPayload?.delta || EMPTY_METRICS;
    const percent = statusPayload?.percent || EMPTY_METRICS;
    const statuses = statusPayload?.status || {};

    return METRIC_CONFIG.map((metric) => {
      const targetValue = toFiniteNumber(targets[metric.key]);
      const realizedValue = toFiniteNumber(realized[metric.key]);
      const backendDelta = toFiniteNumber(delta[metric.key]);
      const backendPercent = normalizePercentValue(percent[metric.key]);
      const fallbackDelta =
        targetValue !== null && realizedValue !== null ? realizedValue - targetValue : null;
      const fallbackPercent =
        targetValue !== null && targetValue !== 0 && realizedValue !== null
          ? normalizePercentValue((realizedValue / targetValue) * 100)
          : null;
      const resolvedPercent = backendPercent ?? fallbackPercent;
      const resolvedStatus = statuses[metric.key]
        || (resolvedPercent !== null ? computeStatusFromPercent(resolvedPercent) : "N/A");

      return {
        ...metric,
        target: targetValue,
        realized: realizedValue,
        delta: backendDelta ?? fallbackDelta,
        percent: resolvedPercent,
        status: resolvedStatus,
      };
    });
  }, [selectedObjective, statusPayload]);

  const metricsByKey = useMemo(() => (
    tableMetrics.reduce((accumulator, metric) => {
      accumulator[metric.key] = metric;
      return accumulator;
    }, {})
  ), [tableMetrics]);

  const executiveMetrics = useMemo(() => (
    [
      metricsByKey.annual_revenue_target,
      metricsByKey.total_resources_end_year,
    ].filter(Boolean)
  ), [metricsByKey]);

  const projection = useMemo(() => {
    const projectedRevenue = toFiniteNumber(cockpitPayload?.forecast?.projected_ca_annual_net);
    const projectedResources = toFiniteNumber(cockpitPayload?.forecast?.projected_resources);
    const actualResources = toFiniteNumber(cockpitPayload?.forecast?.actual_resources);
    const plannedEntries = toFiniteNumber(cockpitPayload?.forecast?.planned_entries);
    const projectedTjm = toFiniteNumber(cockpitPayload?.forecast?.projected_tjm_net);
    const actualTjm = toFiniteNumber(cockpitPayload?.forecast?.actual_tjm_net);
    const actualCa = toFiniteNumber(cockpitPayload?.forecast?.actual_ca_net);

    return {
      projectedRevenue,
      projectedResources,
      actualResources,
      plannedEntries,
      projectedTjm,
      actualTjm,
      actualCa,
      available: projectedRevenue !== null || projectedResources !== null || projectedTjm !== null,
    };
  }, [cockpitPayload]);

  const ceoSummaryCards = useMemo(() => {
    const keys = ["annual_revenue_target", "total_annual_billed_etp"];

    return keys.map((key) => {
      const metric = metricsByKey[key];
      const target = toFiniteNumber(metric?.target);
      const actual = toFiniteNumber(metric?.realized);
      const delta = toFiniteNumber(metric?.delta);
      const percent = toFiniteNumber(metric?.percent);
      const status = String(metric?.status || "N/A");

      if (!metric || target === null || actual === null || percent === null) {
        return null;
      }

      return {
        key,
        label: key === "annual_revenue_target" ? "CA annuel net (réalisé)" : metric.label,
        actualLabel: metric.formatter(actual),
        targetLabel: metric.formatter(target),
        deltaLabel: delta === null
          ? "Ecart: —"
          : `Ecart: ${toSignedValue(delta, metric.formatter)}`,
        progressLabel: `Atteinte: ${formatPct0(percent)}`,
        status,
      };
    }).filter(Boolean);
  }, [metricsByKey]);

  const ceoConfidence = useMemo(() => {
    const confidence = cockpitPayload?.forecast?.confidence;
    if (!confidence) {
      return null;
    }

    const normalizedLevel = String(confidence.level || "").toUpperCase();
    if (!["HIGH", "MEDIUM", "LOW"].includes(normalizedLevel)) {
      return null;
    }

    return {
      level: normalizedLevel,
      detail: String(confidence.detail || ""),
    };
  }, [cockpitPayload]);

  const ceoLandingData = useMemo(() => {
    const revenueTarget = toFiniteNumber(metricsByKey.annual_revenue_target?.target);
    const projectedRevenue = toFiniteNumber(projection.projectedRevenue);

    if (revenueTarget === null || projectedRevenue === null || !ceoConfidence) {
      return null;
    }

    const delta = projectedRevenue - revenueTarget;

    return {
      projectedRevenueLabel: formatEUR0(projectedRevenue),
      deltaLabel: `Ecart vs objectif: ${toSignedValue(delta, formatEUR0)}`,
      confidence: ceoConfidence.level,
      confidenceDetail: ceoConfidence.detail,
    };
  }, [ceoConfidence, metricsByKey, projection.projectedRevenue]);

  const ceoLevers = useMemo(() => {
    const priorities = Array.isArray(cockpitPayload?.priorities) ? cockpitPayload.priorities : [];
    return priorities.map((priority, index) => ({
      id: String(priority?.type || `priority-${index}`).toLowerCase(),
      label: String(priority?.label || "Priorite"),
      valueLabel: String(priority?.detail || ""),
      ctaLabel: String(priority?.cta_label || "Voir"),
      to: String(priority?.cta_path || "/admin/pilotage/dashboard"),
    }));
  }, [cockpitPayload]);

  const ceoKpiCards = useMemo(() => {
    const definitions = [
      {
        key: "tjm",
        metricKey: "tjm_target",
        label: ADMIN_KPI_REGISTRY.tjm_moyen.label,
        actualTitle: "TJM moyen actuel",
        targetTitle: "TJM moyen cible",
        formatter: (value) => formatEUR0(value),
        deltaFormatter: (value) => formatEUR0(value),
        tooltip: "TJM moyen actuel net, après intermédiaire, pondéré par les jours facturés.",
      },
      {
        key: "margin",
        metricKey: "margin_target",
        label: "Marge %",
        actualTitle: "Marge % actuelle",
        targetTitle: "Marge % cible",
        formatter: (value) => formatPct0(value),
        deltaFormatter: (value) => formatPct0(value),
        tooltip: "Marge actuelle nette, calculée sur le CA net après intermédiaire.",
      },
    ];

    return definitions.map((definition) => {
      const metric = metricsByKey[definition.metricKey];
      const target = toFiniteNumber(metric?.target);
      const actual = toFiniteNumber(metric?.realized);
      const delta = toFiniteNumber(metric?.delta);
      const progress = toFiniteNumber(metric?.percent);

      if (target === null || target <= 0 || actual === null) {
        return null;
      }

      return {
        key: definition.key,
        label: definition.label,
        actualTitle: definition.actualTitle || `${definition.label} actuel`,
        targetTitle: definition.targetTitle || `${definition.label} cible`,
        actualLabel: definition.formatter(actual),
        targetLabel: definition.formatter(target),
        deltaLabel: delta === null
          ? "Ecart: —"
          : `Ecart: ${toSignedValue(delta, definition.deltaFormatter)}`,
        progressLabel: `Atteinte: ${progress === null ? "—" : formatPct0(progress)}`,
        status: metric?.status || "N/A",
        tooltip: definition.tooltip,
      };
    }).filter(Boolean);
  }, [metricsByKey]);

  const hasUnavailableCeoKpis = ceoKpiCards.length < 2;
  const isDetailedLoading = loadingObjectives || loadingCockpit;
  const isCeoLoading = isDetailedLoading;

  const openEditModal = (objectiveOrEvent = null, targetField = null) => {
    // When called via onClick, React passes a SyntheticEvent as the first arg.
    // A valid objective always has an `id` field; events do not.
    const objectiveToEdit =
      objectiveOrEvent && typeof objectiveOrEvent === "object" && objectiveOrEvent.id
        ? objectiveOrEvent
        : selectedObjective;

    if (!objectiveToEdit) {
      return;
    }

    setFormError("");
    setFormValues(buildInitialFormValues(objectiveToEdit));
    setEditModalOpen(true);
    if (targetField) {
      setFocusField(targetField);
    }
  };

  const closeEditModal = (nextOpen) => {
    setEditModalOpen(nextOpen);
    if (!nextOpen) {
      setFormError("");
      setFormValues(buildInitialFormValues());
    }
  };

  const openCreateModal = () => {
    setCreateError("");
    setNewObjectiveName("");
    setCreateModalOpen(true);
  };

  const closeCreateModal = (nextOpen) => {
    setCreateModalOpen(nextOpen);
    if (!nextOpen) {
      setCreateError("");
      setNewObjectiveName("");
    }
  };

  const selectExistingObjectiveAfterDuplicate = (objective) => {
    if (!objective?.id) {
      return;
    }

    setActiveObjectiveId(objective.id);
    closeCreateModal(false);
    toast.success("Cet objectif existe déjà. Il a été sélectionné.");
    openEditModal(objective);
  };

  const openRenameModal = () => {
    if (!selectedObjective) {
      return;
    }

    setRenameError("");
    setRenameValue(selectedObjective.name || "");
    setRenameModalOpen(true);
  };

  const closeRenameModal = (nextOpen) => {
    setRenameModalOpen(nextOpen);
    if (!nextOpen) {
      setRenameError("");
      setRenameValue("");
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    if (!selectedObjective?.id) {
      setFormError("Aucun objectif sélectionné.");
      return;
    }

    setSavingEdit(true);
    setFormError("");

    const parsedTargets = {};

    for (const metric of METRIC_CONFIG) {
      const rawValue = String(formValues[metric.key] ?? "").trim();
      if (!rawValue) {
        continue;
      }

      const parsedValue = parseTargetInputValue(metric, rawValue);
      if (!Number.isFinite(parsedValue) || (!metric.allowNegative && parsedValue < 0)) {
        setFormError(`La valeur "${metric.label}" doit être un nombre >= 0.`);
        setSavingEdit(false);
        return;
      }

      parsedTargets[metric.key] = parsedValue;
    }

    const payload = {
      targets: parsedTargets,
      planning_source: "ANNUAL",
      notes: formValues.notes.trim() || null,
    };

    try {
      await updateObjective(selectedObjective.id, payload);
      closeEditModal(false);
      await Promise.all([
        loadYears(),
        refreshObjectives({ preferredObjectiveId: selectedObjective.id }),
      ]);
    } catch (error) {
      setFormError(getApiErrorMessage(error, "Impossible d'enregistrer les objectifs."));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();

    const normalizedName = newObjectiveName.trim();
    if (!normalizedName) {
      setCreateError("Le nom de l'objectif est requis.");
      return;
    }

    setCreatingObjective(true);
    setCreateError("");

    let createdObjective;
    try {
      createdObjective = await createObjective(selectedYear, { name: normalizedName });
    } catch (error) {
      if (isDuplicateObjectiveError(error)) {
        // Real 409 slug conflict: refresh and try to select the existing objective.
        toast.error("Cet objectif existe déjà pour cette année.");
        try {
          const refreshedObjectives = await refreshObjectives();
          // Match by name since slug comparison may differ from server-side slugification.
          const normalizedLower = normalizedName.toLowerCase();
          const objectiveToSelect = refreshedObjectives.find(
            (objective) =>
              objective.name?.toLowerCase() === normalizedLower ||
              objective.slug === slugifyObjectiveName(normalizedName),
          );
          if (objectiveToSelect) {
            selectExistingObjectiveAfterDuplicate(objectiveToSelect);
          } else {
            setCreateError("Cet objectif existe déjà pour cette année. Rechargez la page ou vérifiez la liste.");
          }
        } catch {
          setCreateError("Cet objectif existe déjà pour cette année. Rechargez la page ou vérifiez la liste.");
        }
      } else {
        setCreateError("Impossible de créer l'objectif. Vérifiez les informations puis réessayez.");
        console.error("Erreur lors de la création de l'objectif:", error);
      }
      setCreatingObjective(false);
      return;
    }

    // Creation succeeded — close modal, notify, refresh, open edit.
    closeCreateModal(false);
    toast.success("Objectif créé. Pensez à renseigner ses cibles.");

    const createdObjectiveId = String(createdObjective?.id || "");
    try {
      const [, refreshedObjectives] = await Promise.all([
        loadYears(),
        refreshObjectives({ preferredObjectiveId: createdObjectiveId }),
      ]);
      const objectiveToEdit = (refreshedObjectives || []).find((objective) => objective.id === createdObjectiveId);
      if (objectiveToEdit) {
        openEditModal(objectiveToEdit);
      }
    } catch (_refreshError) {
      // Refresh after creation failed — the objective was created successfully.
      // Non-blocking: just log it, do not show an error to the user.
      console.error("Erreur lors du rafraîchissement après création:", _refreshError);
    } finally {
      setCreatingObjective(false);
    }
  };

  const handleRenameSubmit = async (event) => {
    event.preventDefault();

    if (!selectedObjective?.id) {
      setRenameError("Aucun objectif sélectionné.");
      return;
    }

    const normalizedName = renameValue.trim();
    if (!normalizedName) {
      setRenameError("Le nom de l'objectif est requis.");
      return;
    }

    setRenamingObjective(true);
    setRenameError("");

    try {
      await updateObjective(selectedObjective.id, { name: normalizedName });
      closeRenameModal(false);
      await refreshObjectives({ preferredObjectiveId: selectedObjective.id });
    } catch (error) {
      setRenameError(getApiErrorMessage(error, "Impossible de renommer l'objectif."));
    } finally {
      setRenamingObjective(false);
    }
  };

  const handleDeleteObjective = async () => {
    if (!selectedObjective?.id || objectives.length <= 1 || deletingObjective) {
      return;
    }

    const shouldDelete = window.confirm(
      `Supprimer l'objectif "${selectedObjective.name}" ? Cette action est irréversible.`,
    );
    if (!shouldDelete) {
      return;
    }

    setDeletingObjective(true);
    setErrorMessage("");

    try {
      await deleteObjective(selectedObjective.id);
      await Promise.all([
        loadYears(),
        refreshObjectives(),
      ]);
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, "Impossible de supprimer l'objectif."));
    } finally {
      setDeletingObjective(false);
    }
  };

  if (loadingYears) {
    return (
      <AppLoadingState
        title="Chargement des objectifs"
        description="Récupération des années disponibles..."
        testId="pilotage-objectives-loading"
      />
    );
  }

  if (!selectedYear) {
    return (
      <AppEmptyState
        title="Aucune année disponible"
        description="Aucun objectif annuel n'est encore configuré."
        testId="pilotage-objectives-empty"
      />
    );
  }

  return (
    <div className="space-y-6 min-w-0" data-testid="pilotage-objectives-page">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pilotage
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">Objectifs annuels et mensuels</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400">
              Renseignez l’année, les mois, ou les deux. L’annuel existant reste utilisable ; les écarts avec les mois sont signalés sans bloquer l’enregistrement.
            </p>
            <p className="mt-3 text-xs text-slate-400" data-testid="pilotage-objective-active-label">
              Objectif: <span className="font-semibold text-slate-200">{selectedObjective?.name || "Aucun"}</span>
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <label className="flex flex-col gap-1 text-xs text-slate-400" htmlFor="objectives-year-select">
              Année
              <select
                id="objectives-year-select"
                value={selectedYear}
                onChange={(event) => setSelectedYear(Number(event.target.value))}
                className="h-10 min-w-[130px] rounded-xl border border-white/10 bg-white/[0.03] px-3 text-sm text-white"
                aria-label="Selection de l'annee d'objectifs"
              >
                {years.map((year) => (
                  <option key={year} value={year} className="bg-slate-950">
                    {year}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs text-slate-400" htmlFor="objectives-select">
              Objectif
              <select
                id="objectives-select"
                value={activeObjectiveId}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  if (nextValue === CREATE_OBJECTIVE_SENTINEL) {
                    openCreateModal();
                    return;
                  }
                  setActiveObjectiveId(nextValue);
                }}
                className="h-10 min-w-[220px] rounded-xl border border-white/10 bg-white/[0.03] px-3 text-sm text-white"
                aria-label="Selection de l'objectif actif"
              >
                {objectives.map((objective) => (
                  <option key={objective.id} value={objective.id} className="bg-slate-950">
                    {objective.name}
                  </option>
                ))}
                <option value={CREATE_OBJECTIVE_SENTINEL} className="bg-slate-950">
                  + Nouvel objectif
                </option>
              </select>
            </label>

            <div
              className="inline-flex h-10 items-center rounded-xl border border-white/10 bg-white/[0.03] p-1"
              role="group"
              aria-label="Mode d'affichage des objectifs"
            >
              <button
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isCeoView
                    ? "bg-lenvia-blue/20 text-lenvia-blue"
                    : "text-slate-300 hover:text-white"
                }`}
                onClick={() => setIsCeoView(true)}
                data-testid="pilotage-objectives-ceo-toggle"
                aria-pressed={isCeoView}
              >
                Vue CEO
              </button>
              <button
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  !isCeoView
                    ? "bg-lenvia-blue/20 text-lenvia-blue"
                    : "text-slate-300 hover:text-white"
                }`}
                onClick={() => setIsCeoView(false)}
                data-testid="pilotage-objectives-detail-toggle"
                aria-pressed={!isCeoView}
              >
                Vue detaillee
              </button>
            </div>

            <Button
              type="button"
              onClick={openCreateModal}
              className="btn-primary"
              data-testid="pilotage-objectives-create-button"
            >
              <Plus className="h-4 w-4" />
              Créer un objectif
            </Button>

            <Button
              type="button"
              onClick={openRenameModal}
              variant="outline"
              disabled={!selectedObjective}
              className="border-white/10 bg-white/[0.03] text-white hover:bg-white/10"
              data-testid="pilotage-objectives-rename-button"
            >
              <Pencil className="h-4 w-4" />
              Renommer objectif
            </Button>

            <Button
              type="button"
              onClick={handleDeleteObjective}
              variant="outline"
              disabled={!selectedObjective || objectives.length <= 1 || deletingObjective}
              className="border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20"
              data-testid="pilotage-objectives-delete-button"
            >
              <Trash2 className="h-4 w-4" />
              {deletingObjective ? "Suppression..." : "Supprimer"}
            </Button>

            <MonthlyObjectivesEditor key={selectedObjective?.id || "none"} objective={selectedObjective}
              onSaved={() => refreshObjectives({preferredObjectiveId: selectedObjective?.id})} />

            <Button
              type="button"
              onClick={openEditModal}
              className="btn-primary"
              data-testid="pilotage-objectives-edit-button"
              disabled={!selectedObjective}
              aria-label="Editer les cibles de l'objectif annuel"
              title="Editer les cibles de l'objectif annuel"
            >
              <Pencil className="h-4 w-4" />
              Éditer les cibles
            </Button>
          </div>
        </div>
      </section>

      <ObjectivePlanningReference key={selectedObjective?.id || "reference-none"} objective={selectedObjective} onSaved={() => refreshObjectives({preferredObjectiveId: selectedObjective?.id})} />

      {selectedObjective?.reconciliation?.status && (
        <div role="status" className={`rounded-xl border p-3 text-sm ${planTone(selectedObjective.reconciliation.status)}`} data-testid="objective-plan-consistency">
          {selectedObjective.reconciliation.status === "MISMATCH" && <span aria-hidden="true">● </span>}{PLAN_LABELS[selectedObjective.reconciliation.status]}
          <p className="mt-1 text-xs">Les écarts sont contrôlés ici. Choisissez la référence ci-dessus pour conserver l’annuel ou le remplacer par une consolidation complète.</p>
        </div>
      )}

      {errorMessage ? (
        <AppErrorState
          message={errorMessage}
          actionLabel="Réessayer"
          onAction={() => {
            setErrorMessage("");
            void Promise.all([
              loadYears(),
              refreshObjectives(),
              loadCockpit({
                year: selectedYear,
                objectiveId: activeObjectiveId,
                fallbackObjective: selectedObjective,
              }),
            ]);
          }}
          testId="pilotage-objectives-error"
        />
      ) : null}

      {!loadingObjectives && objectives.length === 0 ? (
        <AppEmptyState
          title="Aucun objectif pour cette année"
          description="Créez votre premier objectif pour commencer le pilotage annuel."
          actionLabel="Créer un objectif"
          onAction={openCreateModal}
          testId="pilotage-objectives-empty-year"
        />
      ) : (
        <div className="space-y-6">
          {isCeoView ? (
            <section className="space-y-6" data-testid="pilotage-objectives-ceo-view">
              {isCeoLoading ? (
                <section className="rounded-3xl border border-white/10 glass p-6" data-testid="pilotage-objectives-ceo-loading">
                  <p className="text-sm text-slate-300">Chargement de la vue CEO...</p>
                </section>
              ) : null}

              <CeoSummaryCards cards={ceoSummaryCards} onEditTarget={key => openEditModal(selectedObjective, key)} />

              <CeoLandingPanel
                projectedRevenueLabel={ceoLandingData?.projectedRevenueLabel || null}
                deltaLabel={ceoLandingData?.deltaLabel || null}
                confidence={ceoLandingData?.confidence || null}
                confidenceDetail={ceoLandingData?.confidenceDetail || null}
              />

              <CeoLeversPanel rows={ceoLevers} />

              <CeoKpisPanel cards={ceoKpiCards} showUnavailableNote={hasUnavailableCeoKpis} onEditTarget={key => openEditModal(selectedObjective, key)} />

              <ChargesFixesRentabilite
                isCeoView={true}
                selectedObjective={selectedObjective}
                objectives={objectives}
                cockpitPayload={cockpitPayload}
                loading={isCeoLoading}
                onEditTarget={key => openEditModal(selectedObjective, key)}
              />

              <Accordion type="single" collapsible className="rounded-3xl border border-white/10 glass px-6">
                <AccordionItem value="details" className="border-none">
                  <AccordionTrigger
                    className="text-base font-semibold text-white hover:no-underline"
                    data-testid="pilotage-objectives-details-trigger"
                  >
                    Détails
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pt-2" data-testid="pilotage-objectives-details-content">
                    <div className="space-y-6">
                      <ObjectivesExecutiveSummary
                        objectiveLabel={selectedObjective?.name || "Aucun"}
                        year={selectedYear}
                        loading={isDetailedLoading}
                        metrics={executiveMetrics}
                      />

                      <ObjectivesPipeline
                        metricsByKey={metricsByKey}
                        loading={isDetailedLoading}
                      />

                      <ObjectivesKpiTable
                        metrics={tableMetrics}
                        loading={isDetailedLoading}
                      />

                      <section
                        className="rounded-3xl border border-white/10 glass p-6"
                        data-testid="pilotage-objectives-projection"
                        aria-labelledby="objectives-projection-title"
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Projection fin d'annee
                        </p>
                        <h2 id="objectives-projection-title" className="mt-2 text-xl font-heading font-semibold text-white">
                          Tendance fin d'annee
                        </h2>
                        <p className="mt-2 text-sm text-slate-400">
                          Estimation fin d'annee basee sur les donnees actuelles de pilotage.
                        </p>

                        {isDetailedLoading ? (
                          <p className="mt-6 text-sm text-slate-400">Chargement de la projection...</p>
                        ) : !projection.available ? (
                          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                            <p className="text-sm font-medium text-white">Projection indisponible</p>
                            <p className="mt-1 text-sm text-slate-400">
                              Les donnees actuelles ne sont pas suffisantes pour estimer la fin d'annee.
                            </p>
                          </div>
                        ) : (
                          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                            {projection.projectedRevenue !== null ? (
                              <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  CA annuel net projeté
                                </p>
                                <p className="mt-3 text-2xl font-semibold text-white">
                                  {formatEUR0(projection.projectedRevenue)}
                                </p>
                                {projection.actualCa !== null ? (
                                  <p className={`mt-2 text-xs font-medium ${(projection.projectedRevenue - projection.actualCa) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                    {toSignedValue(projection.projectedRevenue - projection.actualCa, formatEUR0)} vs réalisé actuel
                                  </p>
                                ) : null}
                              </article>
                            ) : null}

                            {projection.projectedResources !== null ? (
                              <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Ressources projetées fin d'année
                                </p>
                                <p className="mt-3 text-2xl font-semibold text-white">
                                  {formatNumber(projection.projectedResources, { maximumFractionDigits: 1 })}
                                </p>
                                {projection.actualResources !== null || projection.plannedEntries !== null ? (
                                  <p className="mt-2 text-xs text-slate-400">
                                    {projection.actualResources !== null
                                      ? `Actuelles : ${formatNumber(projection.actualResources, { maximumFractionDigits: 1 })}`
                                      : ""}
                                    {projection.actualResources !== null && projection.plannedEntries !== null ? " | " : ""}
                                    {projection.plannedEntries !== null
                                      ? `Entrées prévues : ${formatNumber(projection.plannedEntries, { maximumFractionDigits: 1 })}`
                                      : ""}
                                  </p>
                                ) : null}
                              </article>
                            ) : null}

                            {projection.projectedTjm !== null ? (
                              <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  TJM projeté fin d'année
                                </p>
                                <p className="mt-3 text-2xl font-semibold text-white">
                                  {formatEUR0(projection.projectedTjm)}
                                </p>
                                {projection.actualTjm !== null ? (
                                  <p className={`mt-2 text-xs font-medium ${(projection.projectedTjm - projection.actualTjm) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                    {toSignedValue(projection.projectedTjm - projection.actualTjm, formatEUR0)} vs tjm actuel ({formatEUR0(projection.actualTjm)})
                                  </p>
                                ) : null}
                              </article>
                            ) : null}
                          </div>
                        )}
                      </section>

                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>
          ) : (
            <section className="space-y-6" data-testid="pilotage-objectives-detailed-view">
              <ObjectivesExecutiveSummary
                objectiveLabel={selectedObjective?.name || "Aucun"}
                year={selectedYear}
                loading={isDetailedLoading}
                metrics={executiveMetrics}
              />

              <ObjectivesPipeline
                metricsByKey={metricsByKey}
                loading={isDetailedLoading}
              />

              <ObjectivesKpiTable
                metrics={tableMetrics}
                loading={isDetailedLoading}
                objectiveName={statusPayload?.name || selectedObjective?.name}
              />

              <section
                className="rounded-3xl border border-white/10 glass p-6"
                data-testid="pilotage-objectives-projection"
                aria-labelledby="objectives-projection-title-detailed"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Projection fin d'annee
                </p>
                <h2 id="objectives-projection-title-detailed" className="mt-2 text-xl font-heading font-semibold text-white">
                  Tendance fin d'annee
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  Estimation fin d'annee basee sur les donnees actuelles de pilotage.
                </p>

                {isDetailedLoading ? (
                  <p className="mt-6 text-sm text-slate-400">Chargement de la projection...</p>
                ) : !projection.available ? (
                  <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-sm font-medium text-white">Projection indisponible</p>
                    <p className="mt-1 text-sm text-slate-400">
                      Les donnees actuelles ne sont pas suffisantes pour estimer la fin d'annee.
                    </p>
                  </div>
                ) : (
                  <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {projection.projectedRevenue !== null ? (
                      <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          CA annuel net projeté
                        </p>
                        <p className="mt-3 text-2xl font-semibold text-white">
                          {formatEUR0(projection.projectedRevenue)}
                        </p>
                        {projection.actualCa !== null ? (
                          <p className={`mt-2 text-xs font-medium ${(projection.projectedRevenue - projection.actualCa) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                            {toSignedValue(projection.projectedRevenue - projection.actualCa, formatEUR0)} vs réalisé actuel
                          </p>
                        ) : null}
                      </article>
                    ) : null}

                    {projection.projectedResources !== null ? (
                      <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Ressources projetées fin d'année
                        </p>
                        <p className="mt-3 text-2xl font-semibold text-white">
                          {formatNumber(projection.projectedResources, { maximumFractionDigits: 1 })}
                        </p>
                        {projection.actualResources !== null || projection.plannedEntries !== null ? (
                          <p className="mt-2 text-xs text-slate-400">
                            {projection.actualResources !== null
                              ? `Actuelles : ${formatNumber(projection.actualResources, { maximumFractionDigits: 1 })}`
                              : ""}
                            {projection.actualResources !== null && projection.plannedEntries !== null ? " | " : ""}
                            {projection.plannedEntries !== null
                              ? `Entrées prévues : ${formatNumber(projection.plannedEntries, { maximumFractionDigits: 1 })}`
                              : ""}
                          </p>
                        ) : null}
                      </article>
                    ) : null}

                    {projection.projectedTjm !== null ? (
                      <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          TJM projeté fin d'année
                        </p>
                        <p className="mt-3 text-2xl font-semibold text-white">
                          {formatEUR0(projection.projectedTjm)}
                        </p>
                        {projection.actualTjm !== null ? (
                          <p className={`mt-2 text-xs font-medium ${(projection.projectedTjm - projection.actualTjm) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                            {toSignedValue(projection.projectedTjm - projection.actualTjm, formatEUR0)} vs tjm actuel ({formatEUR0(projection.actualTjm)})
                          </p>
                        ) : null}
                      </article>
                    ) : null}
                  </div>
                )}
              </section>

              <CeoKpisPanel cards={ceoKpiCards} showUnavailableNote={hasUnavailableCeoKpis} onEditTarget={key => openEditModal(selectedObjective, key)} />

              <ChargesFixesRentabilite
                isCeoView={false}
                selectedObjective={selectedObjective}
                objectives={objectives}
                cockpitPayload={cockpitPayload}
                loading={isDetailedLoading}
                onEditTarget={key => openEditModal(selectedObjective, key)}
              />
            </section>
          )}
        </div>
      )}

      <PilotageModal
        open={createModalOpen}
        onOpenChange={closeCreateModal}
        title="Créer un nouvel objectif"
        description="Le slug est généré automatiquement. Vous pourrez ensuite renseigner les cibles."
        onSubmit={handleCreateSubmit}
        submitLabel="Créer"
        isSubmitting={creatingObjective}
      >
        <PilotageField label="Nom de l'objectif" htmlFor="objective-create-name">
          <Input
            id="objective-create-name"
            value={newObjectiveName}
            onChange={(event) => setNewObjectiveName(event.target.value)}
            placeholder="Ex : Réaliste, Ambitieux, Croissance forte"
            maxLength={120}
            data-testid="pilotage-objective-create-name"
          />
        </PilotageField>

        {createError ? <p className="text-sm text-red-300">{createError}</p> : null}
      </PilotageModal>

      <PilotageModal
        open={renameModalOpen}
        onOpenChange={closeRenameModal}
        title="Renommer objectif"
        description="Le slug sera recalculé automatiquement à partir du nouveau nom."
        onSubmit={handleRenameSubmit}
        submitLabel="Renommer"
        isSubmitting={renamingObjective}
      >
        <PilotageField label="Nom objectif" htmlFor="objective-rename-name">
          <Input
            id="objective-rename-name"
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            placeholder="Objectif board"
            maxLength={120}
            data-testid="pilotage-objective-rename-name"
          />
        </PilotageField>

        {renameError ? <p className="text-sm text-red-300">{renameError}</p> : null}
      </PilotageModal>

      <PilotageModal
        open={editModalOpen}
        onOpenChange={closeEditModal}
        title={`Éditer les objectifs ${selectedYear} — ${selectedObjective?.name || "Objectif"}`}
        description="Enregistrer ces cibles active l’annuel saisi. Les objectifs mensuels restent enregistrés séparément ; aucun mois n’est requis."
        onSubmit={handleEditSubmit}
        submitLabel="Enregistrer"
        isSubmitting={savingEdit}
      >
        <div className="grid gap-4 md:grid-cols-2">
          {METRIC_CONFIG.map((metric) => (
            <PilotageField key={metric.key} label={metric.label} hint={metric.hint || null} htmlFor={`objective-${metric.key}`}>
              <Input
                id={`objective-${metric.key}`}
                type={metric.inputType || "number"}
                inputMode={metric.inputMode}
                step={metric.step}
                min={metric.allowNegative ? undefined : "0"}
                placeholder={metric.placeholder}
                value={formValues[metric.key]}
                data-testid={`objective-target-${metric.key}`}
                onChange={(event) => {
                  const { value } = event.target;
                  setFormValues((current) => ({ ...current, [metric.key]: value }));
                }}
              />
            </PilotageField>
          ))}
        </div>

        <PilotageField label="Notes" htmlFor="objective-notes">
          <Textarea
            id="objective-notes"
            rows={4}
            value={formValues.notes}
            onChange={(event) => {
              const { value } = event.target;
              setFormValues((current) => ({ ...current, notes: value }));
            }}
            className="border-white/10 bg-white/[0.03] text-white"
            placeholder="Hypothèses de l'objectif, éléments de contexte..."
          />
        </PilotageField>

        {formError ? <p className="text-sm text-red-300">{formError}</p> : null}
      </PilotageModal>
    </div>
  );
};

export default ObjectivesPage;
